import { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYes, getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import * as apiWarehouses from 'api/apiWarehouses';
import WarehouseNameOffcanvas from '../NameOffcanvas';
import WarehouseApiIdentOffcanvas from '../ApiIdent';
import vconst from 'utils/helpers/structs/vconst';
import SolarTuningLineDuotone from '../../icons/SolarTuningLineDuotone';
import EToolTip from '../../others/EToolTip';
import SvgTrash from '../../svg/SvgTrash';
import { useExportStocks } from '../model/useExportStocks';

interface WarehouseTeamDropdownProps {
    warehouseId: number;
    warehouseIdApi?: string | null;
    showTeamItems?: boolean;
    teamId?: number | null;
    teamName?: string;
    showCustomItems?: boolean;
    customId?: number | null;
    customName?: string;
    warehouseEmpty?: boolean;
    canManage?: boolean;
    onChanged?: () => Promise<void>;
}

const WarehouseTeamDropdown = ({
    warehouseId,
    warehouseIdApi,
    showTeamItems = false,
    teamId,
    teamName = '',
    showCustomItems = false,
    customId,
    customName = '',
    warehouseEmpty = false,
    canManage = false,
    onChanged,
}: WarehouseTeamDropdownProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const { getSettings } = useSettings();
    const navigate = useNavigate();
    const { exportStocks, exporting } = useExportStocks(warehouseId);
    const [showRename, setShowRename] = useState(false);
    const [showCustomRename, setShowCustomRename] = useState(false);
    const [showApiIdent, setShowApiIdent] = useState(false);
    const manageTeamItems = canManage && showTeamItems;
    const manageCustomItems = canManage && showCustomItems;

    const handleMoveTop = () => {
        if (!teamId) return;
        const buttons = [...getButtonsNoYes(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await apiWarehouses.moveTeamToTop(teamId);
            if (res?.stat) {
                await getSettings();
                showToast(t('teamManage.messMovedTop'), 'success');
                return;
            }
            showToast(t('common:messError'), 'danger');
        };
        showDlg({
            type: 'confirm',
            buttons,
            text: t('teamManage.confirmMoveTop', { name: teamName }),
        });
    };

    const handleDelete = () => {
        if (!teamId) return;
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await apiWarehouses.deleteTeam(teamId);
            if (res?.stat) {
                await getSettings();
                showToast(t('teamManage.messDeleted'), 'success');
                navigate(`/warehouse/${vconst.waherouseMainId}`);
                return;
            }

            if (res?.errCode === 'NOT_EMPTY') {
                showToast(t('teamManage.messNotEmpty'), 'danger');
                return;
            }

            showToast(t('common:messError'), 'danger');
        };
        showDlg({
            type: 'confirm',
            buttons,
            text: t('teamManage.confirmDelete', { name: teamName }),
        });
    };

    const handleDeleteCustom = () => {
        if (!customId) return;
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await apiWarehouses.deleteCustomWarehouse(customId);
            if (res?.stat) {
                await getSettings();
                showToast(t('customManage.messDeleted'), 'success');
                navigate(`/warehouse/${vconst.waherouseMainId}`);
                return;
            }

            if (res?.errCode === 'NOT_EMPTY') {
                showToast(t('customManage.messNotEmpty'), 'danger');
                return;
            }

            showToast(t('common:messError'), 'danger');
        };
        showDlg({
            type: 'confirm',
            buttons,
            text: t('customManage.confirmDelete', { name: customName }),
        });
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { disabled?: boolean; danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            disabled={options?.disabled}
            className={[
                options?.disabled ? 'dropdown-item-delete-disabled text-body-secondary' : '',
                options?.danger && !options?.disabled ? 'text-danger' : '',
            ].filter(Boolean).join(' ') || undefined}
            onClick={(e) => {
                if (options?.disabled) return;
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.disabled
                            ? ' text-body-secondary'
                            : options?.danger
                              ? ' text-danger'
                              : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div
                        className={`fw-medium ${
                            options?.disabled
                                ? 'text-body-secondary'
                                : options?.danger
                                  ? 'text-danger'
                                  : ''
                        }`}
                    >
                        {title}
                    </div>
                    <small
                        className={`warehouse-team-menu-hint ${
                            options?.disabled ? 'text-body-secondary' : 'text-muted'
                        }`}
                    >
                        {subtitle}
                    </small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                exporting ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                    <i className="bi bi-file-earmark-excel"></i>
                ),
                t('toolExport'),
                t('toolExportHint'),
                () => {
                    void exportStocks();
                },
                { disabled: exporting },
            )}

            {canManage && (
                <>
                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <i className="bi bi-fingerprint"></i>,
                        t('apiIdent.menu'),
                        t('apiIdent.menuHint'),
                        () => setShowApiIdent(true),
                    )}
                </>
            )}

            {manageTeamItems && (
                <>
                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <i className="bi bi-pencil"></i>,
                        t('teamManage.rename'),
                        t('teamManage.renameHint'),
                        () => setShowRename(true),
                    )}

                    {renderMenuItem(
                        <i className="bi bi-arrow-up"></i>,
                        t('teamManage.moveTop'),
                        t('teamManage.moveTopHint'),
                        handleMoveTop,
                    )}

                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <SvgTrash />,
                        t('teamManage.delete'),
                        t('teamManage.deleteHint'),
                        handleDelete,
                        { disabled: !warehouseEmpty, danger: true },
                    )}
                </>
            )}

            {manageCustomItems && (
                <>
                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <i className="bi bi-pencil"></i>,
                        t('customManage.rename'),
                        t('customManage.renameHint'),
                        () => setShowCustomRename(true),
                    )}

                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <SvgTrash />,
                        t('customManage.delete'),
                        t('customManage.deleteHint'),
                        handleDeleteCustom,
                        { disabled: !warehouseEmpty, danger: true },
                    )}
                </>
            )}
        </Dropdown.Menu>
    );

    return (
        <>
            <EToolTip title={t('teamManage.menuTitle')}>
                <span className="d-inline-flex ms-1">
                    <Dropdown as="span" align="end" className="d-inline-flex">
                        <Dropdown.Toggle
                            as={Button}
                            size="sm"
                            variant="secondary"
                            className="warehouse-team-menu-toggle fs-14 px-2 d-inline-flex align-items-center justify-content-center"
                        >
                            <SolarTuningLineDuotone />
                        </Dropdown.Toggle>

                        {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
                    </Dropdown>
                </span>
            </EToolTip>

            {manageTeamItems && onChanged && (
                <WarehouseNameOffcanvas
                    kind="team"
                    mode="rename"
                    show={showRename}
                    setShow={setShowRename}
                    entityId={teamId || null}
                    initialName={teamName}
                    onSuccess={onChanged}
                />
            )}

            {manageCustomItems && onChanged && (
                <WarehouseNameOffcanvas
                    kind="custom"
                    mode="rename"
                    show={showCustomRename}
                    setShow={setShowCustomRename}
                    entityId={customId || null}
                    initialName={customName}
                    onSuccess={onChanged}
                />
            )}

            {canManage && onChanged && (
                <WarehouseApiIdentOffcanvas
                    show={showApiIdent}
                    setShow={setShowApiIdent}
                    warehouseId={warehouseId}
                    warehouseIdApi={warehouseIdApi}
                    onSaved={onChanged}
                />
            )}
        </>
    );
};

export default WarehouseTeamDropdown;
